import * as XLSX from 'xlsx';
import type { NcReferenceMap } from './types';
// TODO: Убрать отладочные логи
import { dbg } from './debugLog';

// ─── Sheet detection ──────────────────────────────────────────────────────────

/**
 * Expected header keywords for the "Справочник по Нч" sheet.
 * At least 3 of these must appear in the header row for a match.
 */
const HEADER_KEYWORDS = [
  'технологическая операция',
  'ед. изм',
  'скорость операции',
  'нч',
  'потери времени',
  'ресурс',
];

/**
 * Validate the first data row of a candidate sheet.
 * Expected structure:
 *  Col 0 (A): number (№)
 *  Col 1 (B): string (Технологическая операция)
 *  Col 2 (C): string (Ед. изм.)
 *  Col 3 (D): number (Скорость операции)
 *  Col 4 (E): number (Скорость с потерями)
 *  Col 5 (F): number (Нч, мин)
 *  Col 6 (G): percentage or number (Потери %)
 *  Col 7 (H): string (Ресурс/станок)
 */
function isNcDataRow(row: unknown[]): boolean {
  if (!row || row.length < 7) return false;

  // A: number (positive integer)
  const a = Number(row[0]);
  if (!Number.isFinite(a) || a <= 0 || a !== Math.floor(a)) return false;

  // B: non-empty string
  const b = String(row[1] ?? '').trim();
  if (!b) return false;

  // C: string (unit)
  const c = String(row[2] ?? '').trim();
  if (!c) return false;

  // D: number (speed)
  const d = Number(row[3]);
  if (!Number.isFinite(d)) return false;

  // E: number (speed with losses)
  const e = Number(row[4]);
  if (!Number.isFinite(e)) return false;

  // F: number (Нч, мин) — the critical column
  const f = Number(row[5]);
  if (!Number.isFinite(f) || f <= 0) return false;

  // G: percentage or number — flexible
  const g = row[6];
  if (g === '' || g === undefined || g === null) {
    // acceptable — some rows may not have this
  } else {
    const gStr = String(g).replace('%', '').replace(',', '.').trim();
    if (!Number.isFinite(Number(gStr))) return false;
  }

  // H: string (resource) — optional, not checked

  return true;
}

/**
 * Check if a worksheet looks like a "Справочник по Нч" sheet.
 * Validates: header keywords + first data row structure.
 */
function isNcSheet(ws: XLSX.WorkSheet): boolean {
  const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  if (rows.length < 2) {
    // TODO: Убрать отладочные логи — начало
    dbg('  isNcSheet: лист пустой (<2 строк)');
    // TODO: Убрать отладочные логи — конец
    return false;
  }

  // Check header row for keywords
  const headerRow = rows[0].map((c) => String(c ?? '').toLowerCase());
  const matchedKws = HEADER_KEYWORDS.filter((kw) =>
    headerRow.some((h) => h.includes(kw)),
  );
  // TODO: Убрать отладочные логи — начало
  dbg(`  isNcSheet: заголовок = [${rows[0].map(c => String(c ?? '')).join(', ')}]`);
  dbg(`  isNcSheet: совпадений ключевых слов: ${matchedKws.length}/${HEADER_KEYWORDS.length} (${matchedKws.join(', ')})`);
  // TODO: Убрать отладочные логи — конец
  if (matchedKws.length < 3) return false;

  // Check first non-empty data row for structure
  for (let i = 1; i < Math.min(rows.length, 5); i++) {
    if (isNcDataRow(rows[i])) {
      // TODO: Убрать отладочные логи — начало
      dbg(`  isNcSheet: строка данных ${i} прошла валидацию — лист подходит!`);
      // TODO: Убрать отладочные логи — конец
      return true;
    }
    // TODO: Убрать отладочные логи — начало
    dbg(`  isNcSheet: строка данных ${i} не прошла валидацию`);
    // TODO: Убрать отладочные логи — конец
  }

  // TODO: Убрать отладочные логи — начало
  dbg('  isNcSheet: ни одна строка данных не прошла валидацию');
  // TODO: Убрать отладочные логи — конец
  return false;
}

// ─── Parsing ──────────────────────────────────────────────────────────────────

/**
 * Parse the "Справочник по Нч" sheet from an xlsx ArrayBuffer.
 *
 * Search strategy:
 *  1. Sheet named exactly «Справочник по Нч»
 *  2. Sheet with name containing both «Справочник» and «Нч»
 *  3. Any sheet that structurally matches (header keywords + data row shape)
 *
 * Returns a map: operation name → Нч (мин per unit).
 */
export function parseNcReference(buffer: ArrayBuffer): NcReferenceMap {
  const wb = XLSX.read(buffer, { type: 'array' });

  // TODO: Убрать отладочные логи — начало
  dbg(`parseNcReference: открыта книга. Листы: [${wb.SheetNames.join(', ')}]`);
  // TODO: Убрать отладочные логи — конец

  // Strategy 1: exact name
  let ws = wb.Sheets['Справочник по Нч'] ?? null;
  // TODO: Убрать отладочные логи — начало
  dbg(`Стратегия 1 (точное имя «Справочник по Нч»): ${ws ? 'НАЙДЕН' : 'не найден'}`);
  // TODO: Убрать отладочные логи — конец

  // Strategy 2: partial name match
  if (!ws) {
    const partialMatch = wb.SheetNames.find(
      (n) => n.toLowerCase().includes('справочник') && n.toLowerCase().includes('нч'),
    );
    // TODO: Убрать отладочные логи — начало
    dbg(`Стратегия 2 (имя содержит «справочник»+«нч»): ${partialMatch ? `НАЙДЕН: «${partialMatch}»` : 'не найден'}`);
    // TODO: Убрать отладочные логи — конец
    if (partialMatch) ws = wb.Sheets[partialMatch];
  }

  // Strategy 3: structural detection — scan all sheets
  if (!ws) {
    // TODO: Убрать отладочные логи — начало
    dbg('Стратегия 3 (структурная детекция): сканирую все листы...');
    // TODO: Убрать отладочные логи — конец
    for (const name of wb.SheetNames) {
      const candidate = wb.Sheets[name];
      // TODO: Убрать отладочные логи — начало
      dbg(`  Проверяю лист «${name}»...`);
      // TODO: Убрать отладочные логи — конец
      if (isNcSheet(candidate)) {
        ws = candidate;
        // TODO: Убрать отладочные логи — начало
        dbg(`  → Лист «${name}» ПОДОШЁЛ по структуре!`);
        // TODO: Убрать отладочные логи — конец
        break;
      }
    }
  }

  if (!ws) {
    // TODO: Убрать отладочные логи — начало
    dbg('Все стратегии поиска листа исчерпаны — лист не найден');
    // TODO: Убрать отладочные логи — конец
    throw new Error(
      `Лист «Справочник по Нч» не найден. Доступные листы: ${wb.SheetNames.join(', ')}`,
    );
  }

  const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  const map: NcReferenceMap = new Map();

  // Skip header row (row 0), parse data rows
  // TODO: Убрать отладочные логи — начало
  let parsedRows = 0;
  let skippedRows = 0;
  // TODO: Убрать отладочные логи — конец

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 6) {
      // TODO: Убрать отладочные логи — начало
      skippedRows++;
      // TODO: Убрать отладочные логи — конец
      continue;
    }

    const num = Number(row[0]);
    const operation = String(row[1] ?? '').trim();
    const ncMin = Number(row[5]);

    if (!operation || !Number.isFinite(num) || num <= 0) {
      // TODO: Убрать отладочные логи — начало
      skippedRows++;
      // TODO: Убрать отладочные логи — конец
      continue;
    }
    if (!Number.isFinite(ncMin) || ncMin <= 0) {
      // TODO: Убрать отладочные логи — начало
      dbg(`  Строка ${i}: операция="${operation}", Нч=${row[5]} — пропущена (Нч не число или ≤0)`);
      skippedRows++;
      // TODO: Убрать отладочные логи — конец
      continue;
    }

    // First occurrence wins — same operation with different thickness
    // gets separate entries with distinct names (e.g. "Лазерная резка (t до 1 мм)")
    if (!map.has(operation)) {
      map.set(operation, ncMin);
      // TODO: Убрать отладочные логи — начало
      parsedRows++;
      // TODO: Убрать отладочные логи — конец
    } else {
      // TODO: Убрать отладочные логи — начало
      dbg(`  Строка ${i}: операция="${operation}" уже есть, Нч=${ncMin} — пропущена`);
      // TODO: Убрать отладочные логи — конец
    }
  }

  // TODO: Убрать отладочные логи — начало
  dbg(`parseNcReference: итого — ${parsedRows} операций, ${skippedRows} строк пропущено`);
  for (const [op, nc] of map.entries()) {
    dbg(`  → ${op}: Нч=${nc}`);
  }
  // TODO: Убрать отладочные логи — конец

  return map;
}

// ─── Lookup ───────────────────────────────────────────────────────────────────

/**
 * Look up Нч for an operation name.
 * Только точное совпадение строки — никакого fuzzy matching.
 */
export function lookupNc(map: NcReferenceMap, operationName: string): number | null {
  // TODO: Убрать отладочные логи — начало
  const result = map.get(operationName);
  if (result !== undefined) {
    dbg(`lookupNc: "${operationName}" → Нч=${result}`);
  } else {
    dbg(`lookupNc: "${operationName}" → не найдено`);
  }
  // TODO: Убрать отладочные логи — конец
  return result ?? null;
}
